// PreviewSizePreference — プレビュー floating panel (= キャンバス全体ナビ窓) の
// サムネ最大辺サイズ (px) を localStorage に永続化する infrastructure 層
// ユーティリティ.
//
// 背景 (alpha-feedback-2026-05-31.md §8, ID=H / preview-resize.md):
//   - α 期受信フィードバック: 「プレビューウィンドウのサイズを可変にしてほしい
//     (絵のバランス確認時、小さくて見辛い)」.
//   - 方式 = 右下グリップの連続ドラッグリサイズ (preview-resize.md §1).
//     リサイズの実体はサムネ最大辺 (maxEdge) スカラ 1 本なので、永続化対象も
//     その数値 1 本だけ.
//
// 設計 (preview-resize.md §2):
//   - `chatFontScalePreference.ts` と **同流儀** の StorageLike 抽象 + probe +
//     storage-injection test. ただし値は enum でなく **数値 (px)**.
//   - 永続化: localStorage 1 key (`oekaki:preview-size`), user-local のみ,
//     サーバ同期なし, protocol 変更なし.
//   - 読み出し時に `[PREVIEW_MIN_EDGE, PREVIEW_MAX_EDGE]` へ clamp する
//     (= 範囲変更 / 壊れ値 / 旧版が書いた範囲外値への防御).
//   - SSR / Safari private browsing 厳格モードでも getter は default を返すだけ、
//     setter は no-op (= 例外で UI が壊れることはない).
//
// Tests: previewSizePreference.test.ts (storage-injection style,
// chatFontScalePreference.test.ts と同流儀).

import {
  DEFAULT_PREVIEW_EDGE,
  PREVIEW_MAX_EDGE,
  PREVIEW_MIN_EDGE,
} from '../presentation/layouts/classic/previewOverlay.js';

/** localStorage キー (= 外から観察可能な "user data" なので値を固定). */
export const PREVIEW_SIZE_KEY = 'oekaki:preview-size';

/**
 * 値を `[PREVIEW_MIN_EDGE, PREVIEW_MAX_EDGE]` に clamp する純粋関数.
 * 読み出し時 (= 壊れ値 / 範囲変更への防御) に通す.
 */
export const clampPreviewEdge = (edge: number): number =>
  Math.min(PREVIEW_MAX_EDGE, Math.max(PREVIEW_MIN_EDGE, edge));

/**
 * Minimal Storage shape this module uses (chatFontScalePreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PreviewSizePreferenceStore {
  /**
   * 現在のサムネ最大辺 (px) を読み出し. 不在 / 不正 / storage 不可 →
   * `DEFAULT_PREVIEW_EDGE`. 取得値は常に `[MIN, MAX]` へ clamp 済.
   */
  get(): number;
  /** サムネ最大辺 (px) を保存. storage 不可なら no-op (例外を throw しない). */
  set(edge: number): void;
  /** key を削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * chatFontScalePreference.ts の probe を流用 (sentinel write + readback で
 * Safari private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:preview-size__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreatePreviewSizePreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns DEFAULT_PREVIEW_EDGE, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readPreviewEdge = (storage: StorageLike): number => {
  try {
    const raw = storage.getItem(PREVIEW_SIZE_KEY);
    if (raw === null) return DEFAULT_PREVIEW_EDGE;
    // 数値 parse は defensive に (= 旧版や手動編集で混じった壊れ値を弾く).
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return DEFAULT_PREVIEW_EDGE;
    // 範囲外 (= 範囲変更 / 壊れ値) は両端へ clamp.
    return clampPreviewEdge(parsed);
  } catch {
    return DEFAULT_PREVIEW_EDGE;
  }
};

const writePreviewEdge = (storage: StorageLike, edge: number): void => {
  try {
    // 保存時も clamp して範囲外値を書き込まない (= round-trip の一貫性).
    storage.setItem(PREVIEW_SIZE_KEY, String(clampPreviewEdge(Math.round(edge))));
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `PreviewSizePreferenceStore` のインスタンスを構築. storage が unavailable
 * な環境 (SSR / Safari private browsing 厳格モード) でも getter は default を
 * 返すだけで、setter は no-op (= 例外で UI が壊れることはない).
 */
export const createPreviewSizePreferenceStore = (
  opts: CreatePreviewSizePreferenceStoreOptions = {},
): PreviewSizePreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_PREVIEW_EDGE;
      return readPreviewEdge(storage);
    },
    set(edge) {
      if (storage === null) return;
      writePreviewEdge(storage, edge);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(PREVIEW_SIZE_KEY);
      } catch {
        // ignore
      }
    },
  };
};
